export const GALLERY_HASHTAG = 'autoglowgallery'
export const GALLERY_MEDIA_TYPES = ['IMAGE', 'VIDEO', 'CAROUSEL_ALBUM', 'CAROUSEL_ITEM'] as const
export type GalleryMediaType = (typeof GALLERY_MEDIA_TYPES)[number]
export const GALLERY_VISIBILITY = ['auto', 'published', 'hidden'] as const
export type GalleryVisibility = (typeof GALLERY_VISIBILITY)[number]

export interface InstagramMediaRecord {
  id: string
  caption?: string | null
  mediaType: string
  mediaUrl?: string | null
  thumbnailUrl?: string | null
  permalink?: string | null
  timestamp?: string | null
  children?: InstagramMediaRecord[]
}

export interface GalleryMediaRecord {
  source: 'instagram' | 'manual'
  mediaId: string
  parentMediaId?: string | null
  mediaType: GalleryMediaType
  caption: string
  permalink: string | null
  sourceMediaUrl: string | null
  sourceThumbnailUrl: string | null
  cachedUrl: string | null
  publishedAt: string | null
  visibility: GalleryVisibility
  hashtagMatched: boolean
  sourceStatus: 'active' | 'unavailable' | 'deleted'
}

export interface PublicGalleryItem {
  id: string
  src: string
  alt: string
  caption?: string
  permalink?: string | null
  mediaType: GalleryMediaType
  source: 'instagram' | 'manual'
}

export function captionHasGalleryHashtag(caption: string, hashtag = GALLERY_HASHTAG) {
  const needle = `#${hashtag.replace(/^#/, '')}`.toLowerCase()
  return caption.toLowerCase().includes(needle)
}

export function isGalleryItemPublic(item: Pick<GalleryMediaRecord, 'visibility' | 'hashtagMatched' | 'sourceStatus'>) {
  if (item.sourceStatus !== 'active') return false
  if (item.visibility === 'hidden') return false
  if (item.visibility === 'published') return true
  return item.hashtagMatched
}

export function mapInstagramMedia(input: InstagramMediaRecord, existingVisibility?: GalleryVisibility): GalleryMediaRecord[] {
  const mediaType = GALLERY_MEDIA_TYPES.includes(input.mediaType as GalleryMediaType)
    ? input.mediaType as GalleryMediaType
    : 'IMAGE'
  const caption = input.caption?.trim() ?? ''
  const parent: GalleryMediaRecord = {
    source: 'instagram',
    mediaId: input.id,
    parentMediaId: null,
    mediaType,
    caption,
    permalink: input.permalink ?? null,
    sourceMediaUrl: input.mediaUrl ?? input.thumbnailUrl ?? null,
    sourceThumbnailUrl: input.thumbnailUrl ?? input.mediaUrl ?? null,
    cachedUrl: null,
    publishedAt: input.timestamp ?? null,
    visibility: existingVisibility ?? 'auto',
    hashtagMatched: captionHasGalleryHashtag(caption),
    sourceStatus: 'active',
  }

  if (mediaType !== 'CAROUSEL_ALBUM' || !input.children?.length) {
    return [parent]
  }

  return [
    parent,
    ...input.children.map((child) => ({
      source: 'instagram' as const,
      mediaId: child.id,
      parentMediaId: input.id,
      mediaType: (child.mediaType === 'VIDEO' ? 'VIDEO' : 'CAROUSEL_ITEM') as GalleryMediaType,
      caption,
      permalink: input.permalink ?? null,
      sourceMediaUrl: child.mediaUrl ?? child.thumbnailUrl ?? null,
      sourceThumbnailUrl: child.thumbnailUrl ?? child.mediaUrl ?? null,
      cachedUrl: null,
      publishedAt: input.timestamp ?? null,
      visibility: existingVisibility ?? 'auto',
      hashtagMatched: parent.hashtagMatched,
      sourceStatus: 'active' as const,
    })),
  ]
}

export function dedupeGalleryRecords(records: GalleryMediaRecord[]) {
  const seen = new Set<string>()
  return records.filter((record) => {
    const key = `${record.source}:${record.mediaId}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function mergePublicGallery(input: {
  staticItems: PublicGalleryItem[]
  socialItems: GalleryMediaRecord[]
}): PublicGalleryItem[] {
  const social = dedupeGalleryRecords(
    input.socialItems.filter((item) => item.parentMediaId == null && isGalleryItemPublic(item)),
  )
    .map((item) => ({
      id: item.mediaId,
      src: item.cachedUrl || item.sourceThumbnailUrl || item.sourceMediaUrl || '',
      alt: item.caption || 'Autoglow studio work from Instagram',
      caption: item.caption || undefined,
      permalink: item.permalink,
      mediaType: item.mediaType,
      source: item.source,
    }))
    .filter((item) => item.src)

  const staticItems = input.staticItems.map((item) => ({
    ...item,
    source: 'manual' as const,
  }))
  return [...social, ...staticItems]
}

export function applySyncUpdates(input: {
  existing: GalleryMediaRecord[]
  incoming: GalleryMediaRecord[]
  seenIds: string[]
}) {
  const existingById = new Map(input.existing.map((item) => [item.mediaId, item]))
  const upserts = input.incoming.map((item) => {
    const previous = existingById.get(item.mediaId)
    return {
      ...item,
      visibility: previous?.visibility ?? item.visibility,
      cachedUrl: previous?.cachedUrl ?? item.cachedUrl,
    }
  })
  const missing = input.existing
    .filter((item) => item.source === 'instagram' && !input.seenIds.includes(item.mediaId))
    .map((item) => ({ ...item, sourceStatus: 'unavailable' as const }))
  return { upserts: dedupeGalleryRecords(upserts), missing }
}

export const META_GALLERY_SCOPES = [
  'pages_show_list',
  'pages_read_engagement',
  'instagram_basic',
].join(',')

export function metaOAuthSetupInstructions() {
  return [
    'Create a Meta app with Instagram and Facebook Login for Business.',
    'Redirect URI must point at the meta-oauth-callback Edge Function.',
    `Request scopes: ${META_GALLERY_SCOPES}.`,
    'Store META_APP_ID, META_APP_SECRET, and META_REDIRECT_URI in Supabase Edge Function secrets.',
    'Lyle then connects the Facebook Page that is linked to the Instagram Professional account.',
    'Posts containing #autoglowgallery auto-publish; other posts import hidden until shown in admin.',
  ].join(' ')
}
